/**
 * OnboardingLayout — Shell for the 4-step onboarding wizard.
 *
 * Replicates the OnbShell from screens-onb.jsx design:
 *   - Brand bar at top
 *   - Stepper with 4 steps
 *   - Current workspace name
 *   - Body slot + footer with Back / Next
 *
 * Uses auth.css class names (onb-screen, onb-stepper, etc.) translated to
 * Tailwind equivalents so the file is self-contained.
 */

import React, { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowLeft, ArrowRight, SkipForward } from 'lucide-react';
import { Button } from '@shared/ui/shadcn/ui/button';
import { useOnboarding } from '../contexts/OnboardingContext';

// ── Types ────────────────────────────────────────────────────────────────────

interface OnboardingLayoutProps {
  /** Current step 1-4 */
  step: number;
  children: ReactNode;

  /** Footer actions */
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  loading?: boolean;

  /** Optional skip action (left side of footer) */
  skipLabel?: string;
  onSkip?: () => void;

  /** Override max-width of the content area */
  contentMaxWidth?: number;
}

// ── Steps config ─────────────────────────────────────────────────────────────

const STEPS = [
  { n: 1, label: 'Datos fiscales' },
  { n: 2, label: 'Equipo' },
  { n: 3, label: 'Tu negocio' },
  { n: 4, label: 'Modulos' },
];

// ── Component ────────────────────────────────────────────────────────────────

const OnboardingLayout: React.FC<OnboardingLayoutProps> = ({
  step,
  children,
  onBack,
  onNext,
  nextLabel = 'Continuar',
  nextDisabled = false,
  loading = false,
  skipLabel,
  onSkip,
  contentMaxWidth,
}) => {
  const { state } = useOnboarding();

  return (
    <div className="w-full h-full bg-background flex flex-col overflow-hidden">
      {/* ── Top bar: brand + stepper + workspace name ── */}
      <div className="px-6 sm:px-10 pt-5 flex items-center justify-between">
        {/* Brand */}
        <div className="flex items-center gap-2.5 font-semibold text-base tracking-tight select-none">
          <span className="w-2.5 h-2.5 rounded-full bg-primary" />
          <span>
            ThePlatform<span className="text-primary">360</span>
          </span>
        </div>

        {/* Stepper */}
        <div className="hidden sm:flex items-center gap-2 flex-1 max-w-[540px] mx-auto">
          {STEPS.map((s, i) => {
            const done = step > s.n;
            const active = step === s.n;
            const last = i === STEPS.length - 1;

            return (
              <div key={s.n} className="flex items-center gap-2.5 flex-1">
                {/* Circle */}
                <span
                  className={[
                    'w-[26px] h-[26px] rounded-full flex items-center justify-center text-xs font-bold font-mono flex-shrink-0',
                    done
                      ? 'bg-primary text-primary-foreground border-primary'
                      : active
                        ? 'bg-background text-primary border-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.15)]'
                        : 'bg-card text-muted-foreground border-border',
                    'border-[1.5px]',
                  ].join(' ')}
                >
                  {done ? <Check className="w-3.5 h-3.5" /> : s.n}
                </span>

                {/* Label */}
                <span
                  className={[
                    'text-xs font-medium whitespace-nowrap',
                    done || active ? (active ? 'text-primary' : 'text-foreground') : 'text-muted-foreground',
                  ].join(' ')}
                >
                  {s.label}
                </span>

                {/* Connector line */}
                {!last && (
                  <span
                    className={[
                      'flex-1 h-px mx-1',
                      done ? 'bg-primary' : 'bg-border',
                    ].join(' ')}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Workspace badge (mobile: show step x/4) */}
        <div className="text-sm text-muted-foreground">
          <span className="hidden sm:inline">
            Workspace:{' '}
            <strong className="text-foreground">{state.workspaceName || 'Mi empresa'}</strong>
          </span>
          <span className="sm:hidden font-mono text-xs">
            Paso {step}/4
          </span>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 px-6 sm:px-10 pt-9 pb-6 flex flex-col items-center overflow-auto">
        <div
          className="w-full flex flex-col gap-2 flex-1"
          style={{ maxWidth: contentMaxWidth ?? 640 }}
        >
          {children}
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="px-6 sm:px-10 py-4 border-t bg-card flex items-center justify-between">
        {/* Left: skip */}
        <div>
          {skipLabel && onSkip ? (
            <button
              type="button"
              onClick={onSkip}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              {skipLabel} <SkipForward className="w-3.5 h-3.5" />
            </button>
          ) : (
            <span />
          )}
        </div>

        {/* Right: back + next */}
        <div className="flex items-center gap-2">
          {onBack && (
            <Button variant="outline" size="sm" onClick={onBack} disabled={loading}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Atras
            </Button>
          )}
          {onNext && (
            <Button size="sm" onClick={onNext} disabled={nextDisabled || loading}>
              {loading ? 'Guardando...' : nextLabel}
              {!loading && <ArrowRight className="w-4 h-4 ml-1" />}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingLayout;
